import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Zap, Mail, Lock, Eye, EyeOff, ArrowRight, Package } from 'lucide-react';

export default function CustomerLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate login
    setTimeout(() => {
      if (email && password) {
        navigate('/customer/dashboard');
      } else {
        setError('Please enter valid credentials');
      }
      setIsLoading(false);
    }, 1000);
  };

  return (
    <div className="customer-login-page" data-testid="customer-login-page">
      <div className="ambient-glow"></div>
      
      {/* Left Side - Branding */}
      <div className="login-branding">
        <div className="branding-content">
          <div className="logo-mark">
            <Zap size={40} />
          </div>
          <h1>Bolt Nexus</h1>
          <p className="tagline">Your packages, tracked and managed</p>
          
          <div className="feature-highlights">
            <div className="highlight-item">
              <Package size={24} />
              <div>
                <h4>Real-time Tracking</h4>
                <p>Track your packages from Florida to Jamaica</p>
              </div>
            </div>
            <div className="highlight-item">
              <Mail size={24} />
              <div>
                <h4>Instant Notifications</h4>
                <p>Get updates via WhatsApp and Email</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="login-form-container">
        <div className="login-card">
          <div className="login-header">
            <h2>Welcome Back</h2>
            <p>Sign in to your customer portal</p>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            {error && (
              <div className="error-message" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <Mail size={18} />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <Lock size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  data-testid="password-input"
                />
                <button 
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-options">
              <label className="remember-me">
                <input type="checkbox" data-testid="remember-me" />
                <span>Remember me</span>
              </label>
              <a href="#" className="forgot-password">Forgot password?</a>
            </div>

            <button 
              type="submit" 
              className="btn btn-primary login-btn"
              disabled={isLoading}
              data-testid="login-submit"
            >
              {isLoading ? (
                <>Signing in...</>
              ) : (
                <>
                  Sign In <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className="login-footer">
            <p>Don't have an account? <a href="#">Contact your courier</a></p>
          </div>

          {/* Demo credentials hint */}
          <div className="demo-hint">
            <p>Demo: Enter any email and password to continue</p>
          </div>
        </div>

        {/* Track without login */}
        <div className="track-package-link">
          <Link to="/customer/tracking" className="track-link" data-testid="track-without-login">
            <Package size={16} />
            <span>Track a package without signing in</span>
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
